import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function AgentAccountOpening() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("open"); // open, search
  
  // Data
  const [branches, setBranches] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  
  // Agent info
  const [agentInfo, setAgentInfo] = useState(null);
  
  // Forms
  const [searchQuery, setSearchQuery] = useState("");
  const [accountForm, setAccountForm] = useState({
    fullName: "", email: "", phoneNumber: "", dateOfBirth: "", gender: "",
    bvn: "", address: "", city: "", state: "", accountTypeId: "", branchId: "", initialDeposit: 0
  });

  // KYC Documents
  const [kycDocuments, setKycDocuments] = useState({
    passport: null,
    idCard: null,
    utilityBill: null,
    signature: null
  });
  const [kycPreviews, setKycPreviews] = useState({
    passport: null,
    idCard: null,
    utilityBill: null,
    signature: null
  });

  const nigerianStates = ["Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "FCT", "Gombe", "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara", "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara"];

  const documentTypes = [
    { key: "passport", label: "Passport Photograph", required: true, icon: "📷" },
    { key: "idCard", label: "Valid ID (NIN/Voter's Card/Driver's License)", required: true, icon: "🪪" },
    { key: "utilityBill", label: "Utility Bill / Proof of Address", required: false, icon: "📄" },
    { key: "signature", label: "Signature Specimen", required: true, icon: "✍️" }
  ];

  const [branchLoading, setBranchLoading] = useState(true);
  const [typesLoading, setTypesLoading] = useState(true);

  useEffect(() => {
    loadInitialData();
    loadAgentInfo();
  }, []);

  const loadInitialData = async () => {
    // Load account types
    setTypesLoading(true);
    try {
      const typesRes = await api.get("/BankAccount/types");
      console.log("Account types response:", typesRes.data);
      const typesData = Array.isArray(typesRes.data) ? typesRes.data : [];
      setAccountTypes(typesData.filter(t => t.isActive !== false && t.shortCode !== "FD"));
    } catch (err) {
      console.error("Failed to load account types:", err);
    } finally {
      setTypesLoading(false);
    }
    
    // Load branches
    setBranchLoading(true);
    try {
      const branchesRes = await api.get("/Branch");
      console.log("Branches response:", branchesRes.data);
      const branchData = Array.isArray(branchesRes.data) ? branchesRes.data : [];
      const activeBranches = branchData.filter(b => b.isActive !== false);
      if (activeBranches.length > 0) {
        setBranches(activeBranches);
      } else {
        throw new Error("No active branches found");
      }
    } catch (branchErr) {
      console.warn("Could not load branches, using fallback:", branchErr);
      // Fallback: Get agent's branch from user info or use default branches
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      console.log("User from localStorage:", user);
      
      if (user.branchId) {
        // Try to get single branch
        try {
          const singleBranchRes = await api.get(`/Branch/${user.branchId}`);
          setBranches([singleBranchRes.data]);
        } catch (e) {
          // Use user's branch info if available
          setBranches([{ 
            id: user.branchId, 
            code: user.branchCode || "001", 
            name: user.branchName || "Head Office",
            isActive: true 
          }]);
        }
      } else {
        // Last resort - provide default branch options
        // You should update this with your actual branch data
        setBranches([
          { id: 1, code: "001", name: "Head Office", isActive: true },
          { id: 2, code: "002", name: "Lagos Branch", isActive: true },
          { id: 3, code: "003", name: "Abuja Branch", isActive: true }
        ]);
      }
    } finally {
      setBranchLoading(false);
    }
  };

  const loadAgentInfo = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setAgentInfo(user);
    // Set default branch to agent's branch if available
    if (user.branchId) {
      setAccountForm(prev => ({ ...prev, branchId: user.branchId.toString() }));
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 3) {
      alert("Enter at least 3 characters to search");
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(`/AccountOpening/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data || []);
    } catch (err) {
      console.error("Search error:", err);
      alert("Search failed");
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const res = await api.get(`/BankAccount/customer/${customer.id}`);
      setCustomerAccounts(res.data || []);
    } catch (err) {
      setCustomerAccounts([]);
    }
  };

  const handleFileChange = (docType, file) => {
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed");
        return;
      }

      setKycDocuments(prev => ({ ...prev, [docType]: file }));
      
      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setKycPreviews(prev => ({ ...prev, [docType]: reader.result }));
        };
        reader.readAsDataURL(file);
      } else {
        setKycPreviews(prev => ({ ...prev, [docType]: "pdf" }));
      }
    }
  };

  const removeDocument = (docType) => {
    setKycDocuments(prev => ({ ...prev, [docType]: null }));
    setKycPreviews(prev => ({ ...prev, [docType]: null }));
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!accountForm.fullName || !accountForm.phoneNumber) {
      alert("Full name and phone number are required");
      return;
    }
    if (!accountForm.accountTypeId || !accountForm.branchId) {
      alert("Please select account type and branch");
      return;
    }

    // Validate required KYC documents
    const requiredDocs = documentTypes.filter(d => d.required);
    const missingDocs = requiredDocs.filter(d => !kycDocuments[d.key]);
    if (missingDocs.length > 0) {
      alert(`Please upload required documents: ${missingDocs.map(d => d.label).join(", ")}`);
      return;
    }

    const selectedType = accountTypes.find(t => t.id === parseInt(accountForm.accountTypeId));
    if (selectedType && accountForm.initialDeposit < selectedType.minimumBalance) {
      alert(`Minimum deposit for ${selectedType.name} is ₦${selectedType.minimumBalance.toLocaleString()}`);
      return;
    }

    try {
      setLoading(true);
      
      // Step 1: Create customer
      // Generate unique temp email if not provided
      const tempEmail = accountForm.email || `${accountForm.phoneNumber}_${Date.now()}@temp.uptech.com`;
      
      const createRes = await api.post("/AccountOpening/create", {
        fullName: accountForm.fullName,
        email: tempEmail,
        phoneNumber: accountForm.phoneNumber,
        dateOfBirth: accountForm.dateOfBirth || null,
        gender: accountForm.gender,
        bvn: accountForm.bvn,
        address: accountForm.address,
        city: accountForm.city,
        state: accountForm.state,
        country: "Nigeria",
        branchId: parseInt(accountForm.branchId),
        isAgent: false
      });

      const userId = createRes.data.userId;

      // Step 2: Open NUBAN account using agent-specific endpoint (bypasses KYC check)
      let nubanRes;
      try {
        // Use agent-specific endpoint that bypasses KYC validation
        nubanRes = await api.post("/BankAccount/open-by-agent", {
          userId: userId,
          accountTypeId: parseInt(accountForm.accountTypeId),
          branchId: parseInt(accountForm.branchId),
          initialDeposit: parseFloat(accountForm.initialDeposit) || 0
        });
      } catch (accountErr) {
        // If agent endpoint fails, try regular endpoint with skipKycCheck flag
        console.error("Agent endpoint failed, trying alternative:", accountErr);
        try {
          nubanRes = await api.post("/BankAccount/open", {
            userId: userId,
            accountTypeId: parseInt(accountForm.accountTypeId),
            branchId: parseInt(accountForm.branchId),
            initialDeposit: parseFloat(accountForm.initialDeposit) || 0,
            skipKycCheck: true,
            openedByAgent: true,
            agentId: agentInfo?.id
          });
        } catch (e) {
          throw accountErr; // Throw original error
        }
      }

      // Step 3: Upload KYC documents
      const uploadPromises = [];
      for (const [docType, file] of Object.entries(kycDocuments)) {
        if (file) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("userId", userId);
          formData.append("documentType", docType);
          formData.append("uploadedBy", agentInfo?.id || "");
          
          uploadPromises.push(
            api.post("/KYC/upload", formData, {
              headers: { "Content-Type": "multipart/form-data" }
            }).catch(err => {
              console.error(`Failed to upload ${docType}:`, err);
              return null;
            })
          );
        }
      }

      await Promise.all(uploadPromises);

      alert(`✅ Account Created Successfully!\n\n👤 Customer: ${accountForm.fullName}\n🏦 Account: ${nubanRes.data.account?.accountNumber || "Created"}\n📝 Type: ${selectedType?.name}\n💰 Initial Deposit: ₦${accountForm.initialDeposit.toLocaleString()}\n\n⚠️ Status: PENDING KYC APPROVAL\nThe account will be activated after backoffice approves the KYC documents.`);

      // Reset form
      setAccountForm({
        fullName: "", email: "", phoneNumber: "", dateOfBirth: "", gender: "",
        bvn: "", address: "", city: "", state: "", accountTypeId: "", 
        branchId: agentInfo?.branchId?.toString() || "", initialDeposit: 0
      });
      setKycDocuments({ passport: null, idCard: null, utilityBill: null, signature: null });
      setKycPreviews({ passport: null, idCard: null, utilityBill: null, signature: null });

    } catch (err) {
      console.error("Account creation error:", err);
      alert(err.response?.data?.message || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString()}`;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>← Back</button>
        <div>
          <h1 style={styles.title}>🏦 Agent Account Opening</h1>
          <p style={styles.subtitle}>Open accounts for customers (Pending KYC approval)</p>
        </div>
        {agentInfo && (
          <div style={styles.agentBadge}>
            <span style={styles.agentIcon}>👤</span>
            <div>
              <div style={styles.agentName}>{agentInfo.fullName}</div>
              <div style={styles.agentRole}>Agent</div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button 
          onClick={() => setActiveTab("open")} 
          style={{ ...styles.tab, ...(activeTab === "open" ? styles.tabActive : {}) }}
        >
          <span style={styles.tabIcon}>➕</span>
          <span>Open Account</span>
        </button>
        <button 
          onClick={() => setActiveTab("search")} 
          style={{ ...styles.tab, ...(activeTab === "search" ? styles.tabActive : {}) }}
        >
          <span style={styles.tabIcon}>🔍</span>
          <span>Find Customer</span>
        </button>
      </div>

      {/* Open Account Tab */}
      {activeTab === "open" && (
        <div style={styles.content}>
          <form onSubmit={handleCreateAccount}>
            {/* Notice Banner */}
            <div style={styles.noticeBanner}>
              <span style={styles.noticeIcon}>ℹ️</span>
              <div>
                <strong>Account Activation Notice</strong>
                <p style={{ margin: "5px 0 0", fontSize: "13px" }}>
                  Accounts opened by agents require KYC document verification. The account will remain in <strong>Pending</strong> status until approved by backoffice staff.
                </p>
              </div>
            </div>

            {/* Account Type Selection */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>🏦 Account Type & Branch</h3>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Account Type *</label>
                  <select
                    value={accountForm.accountTypeId}
                    onChange={(e) => setAccountForm({ ...accountForm, accountTypeId: e.target.value })}
                    style={styles.select}
                    required
                    disabled={typesLoading}
                  >
                    <option value="">Select Account Type</option>
                    {typesLoading ? (
                      <option value="" disabled>Loading account types...</option>
                    ) : accountTypes.length > 0 ? (
                      accountTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.classCode || type.code} - {type.name} (Min: {formatCurrency(type.minimumBalance)})
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No account types available</option>
                    )}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Branch *</label>
                  <select
                    value={accountForm.branchId}
                    onChange={(e) => setAccountForm({ ...accountForm, branchId: e.target.value })}
                    style={styles.select}
                    required
                    disabled={branchLoading}
                  >
                    <option value="">Select Branch</option>
                    {branchLoading ? (
                      <option value="" disabled>Loading branches...</option>
                    ) : branches.length > 0 ? (
                      branches.map(branch => (
                        <option key={branch.id} value={branch.id}>
                          {branch.code} - {branch.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No branches available</option>
                    )}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Initial Deposit (₦)</label>
                  <input
                    type="number"
                    value={accountForm.initialDeposit}
                    onChange={(e) => setAccountForm({ ...accountForm, initialDeposit: parseFloat(e.target.value) || 0 })}
                    style={styles.input}
                    placeholder="0"
                  />
                </div>
              </div>
              {accountForm.accountTypeId && (() => {
                const type = accountTypes.find(t => t.id === parseInt(accountForm.accountTypeId));
                return type ? (
                  <div style={styles.typeInfo}>
                    <span>💰 Interest: {type.interestRate || 0}%</span>
                    <span>📊 Daily Limit: {formatCurrency(type.dailyTransactionLimit)}</span>
                    <span>📋 Min Balance: {formatCurrency(type.minimumBalance)}</span>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Customer Information */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>👤 Customer Information</h3>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Full Name *</label>
                  <input
                    type="text"
                    value={accountForm.fullName}
                    onChange={(e) => setAccountForm({ ...accountForm, fullName: e.target.value })}
                    style={styles.input}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Phone Number *</label>
                  <input
                    type="tel"
                    value={accountForm.phoneNumber}
                    onChange={(e) => setAccountForm({ ...accountForm, phoneNumber: e.target.value })}
                    style={styles.input}
                    placeholder="08012345678"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email (Optional)</label>
                  <input
                    type="email"
                    value={accountForm.email}
                    onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                    style={styles.input}
                    placeholder="customer@email.com"
                  />
                </div>
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date of Birth</label>
                  <input
                    type="date"
                    value={accountForm.dateOfBirth}
                    onChange={(e) => setAccountForm({ ...accountForm, dateOfBirth: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Gender</label>
                  <select
                    value={accountForm.gender}
                    onChange={(e) => setAccountForm({ ...accountForm, gender: e.target.value })}
                    style={styles.select}
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>BVN</label>
                  <input
                    type="text"
                    value={accountForm.bvn}
                    onChange={(e) => setAccountForm({ ...accountForm, bvn: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                    style={styles.input}
                    placeholder="11-digit BVN"
                    maxLength="11"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>📍 Address</h3>
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: 2 }}>
                  <label style={styles.label}>Street Address</label>
                  <input
                    type="text"
                    value={accountForm.address}
                    onChange={(e) => setAccountForm({ ...accountForm, address: e.target.value })}
                    style={styles.input}
                    placeholder="Enter address"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>City</label>
                  <input
                    type="text"
                    value={accountForm.city}
                    onChange={(e) => setAccountForm({ ...accountForm, city: e.target.value })}
                    style={styles.input}
                    placeholder="City"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>State</label>
                  <select
                    value={accountForm.state}
                    onChange={(e) => setAccountForm({ ...accountForm, state: e.target.value })}
                    style={styles.select}
                  >
                    <option value="">Select State</option>
                    {nigerianStates.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* KYC Documents */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>📁 KYC Documents</h3>
              <p style={styles.kycHint}>Upload clear photos or scanned copies. Max 5MB per file. Accepted formats: JPG, PNG, PDF</p>
              
              <div style={styles.documentsGrid}>
                {documentTypes.map(doc => (
                  <div key={doc.key} style={styles.documentItem}>
                    <div style={styles.documentHeader}>
                      <span style={styles.documentIcon}>{doc.icon}</span>
                      <span style={styles.documentLabel}>
                        {doc.label} {doc.required && <span style={styles.requiredMark}>*</span>}
                      </span>
                    </div>
                    
                    {kycPreviews[doc.key] ? (
                      <div style={styles.previewContainer}>
                        {kycPreviews[doc.key] === "pdf" ? (
                          <div style={styles.pdfPreview}>
                            <span style={styles.pdfIcon}>📄</span>
                            <span>{kycDocuments[doc.key]?.name}</span>
                          </div>
                        ) : (
                          <img src={kycPreviews[doc.key]} alt={doc.label} style={styles.imagePreview} />
                        )}
                        <button 
                          type="button"
                          onClick={() => removeDocument(doc.key)} 
                          style={styles.removeBtn}
                        >
                          ✕ Remove
                        </button>
                      </div>
                    ) : (
                      <label style={styles.uploadArea}>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileChange(doc.key, e.target.files[0])}
                          style={{ display: "none" }}
                        />
                        <div style={styles.uploadContent}>
                          <span style={styles.uploadIcon}>📤</span>
                          <span>Click to upload</span>
                        </div>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Submit */}
            <div style={styles.submitSection}>
              <button type="submit" disabled={loading} style={styles.submitBtn}>
                {loading ? "Creating Account..." : "🏦 Create Account (Pending Approval)"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search Customer Tab */}
      {activeTab === "search" && (
        <div style={styles.content}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🔍 Find Customer</h3>
            <p style={styles.searchHint}>Search by name, phone number, email, or account number</p>
            <div style={styles.searchBox}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={styles.searchInput}
                placeholder="Enter search query..."
              />
              <button onClick={handleSearch} disabled={loading} style={styles.searchBtn}>
                {loading ? "..." : "Search"}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div style={styles.resultsSection}>
                <h4 style={styles.resultsTitle}>Search Results ({searchResults.length})</h4>
                <div style={styles.resultsList}>
                  {searchResults.map(customer => (
                    <div key={customer.id} style={styles.customerCard}>
                      <div style={styles.customerInfo}>
                        <div style={styles.customerAvatar}>
                          {customer.fullName?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={styles.customerName}>{customer.fullName}</div>
                          <div style={styles.customerDetails}>
                            📱 {customer.phoneNumber || "N/A"} • ✉️ {customer.email}
                          </div>
                          {customer.accountNumber && (
                            <div style={styles.customerAccount}>
                              🏦 {customer.accountNumber}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={styles.customerActions}>
                        <button
                          onClick={() => {
                            setAccountForm(prev => ({
                              ...prev,
                              fullName: customer.fullName,
                              email: customer.email,
                              phoneNumber: customer.phoneNumber || ""
                            }));
                            setActiveTab("open");
                          }}
                          style={styles.selectBtn}
                        >
                          🏦 Open Account
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {searchResults.length === 0 && searchQuery.length >= 3 && !loading && (
              <div style={styles.noResults}>
                <span style={styles.noResultsIcon}>🔍</span>
                <p>No customers found</p>
                <button 
                  onClick={() => setActiveTab("open")} 
                  style={styles.createNewBtn}
                >
                  ➕ Create New Customer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  header: {
    background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
    color: "white",
    padding: "20px 30px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
    flexWrap: "wrap"
  },
  backBtn: {
    padding: "10px 20px",
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "500"
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0
  },
  subtitle: {
    fontSize: "14px",
    opacity: 0.8,
    margin: "5px 0 0"
  },
  agentBadge: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "rgba(255,255,255,0.15)",
    padding: "10px 15px",
    borderRadius: "10px"
  },
  agentIcon: {
    fontSize: "24px"
  },
  agentName: {
    fontWeight: "600",
    fontSize: "14px"
  },
  agentRole: {
    fontSize: "11px",
    opacity: 0.8
  },
  tabs: {
    display: "flex",
    backgroundColor: "white",
    padding: "10px",
    gap: "10px",
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap"
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 24px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#64748b",
    transition: "all 0.2s"
  },
  tabActive: {
    backgroundColor: "#1e3a5f",
    color: "white"
  },
  tabIcon: {
    fontSize: "16px"
  },
  content: {
    maxWidth: "1000px",
    margin: "0 auto",
    padding: "20px"
  },
  noticeBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "15px",
    padding: "15px 20px",
    backgroundColor: "#fef3c7",
    border: "1px solid #f59e0b",
    borderRadius: "10px",
    marginBottom: "20px",
    color: "#92400e"
  },
  noticeIcon: {
    fontSize: "24px"
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e3a5f",
    marginBottom: "15px",
    paddingBottom: "10px",
    borderBottom: "1px solid #e2e8f0"
  },
  formRow: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap"
  },
  formGroup: {
    flex: 1,
    minWidth: "200px",
    marginBottom: "15px"
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "6px"
  },
  input: {
    width: "100%",
    padding: "12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box",
    transition: "border-color 0.2s"
  },
  select: {
    width: "100%",
    padding: "12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box",
    backgroundColor: "white",
    cursor: "pointer"
  },
  typeInfo: {
    display: "flex",
    gap: "20px",
    marginTop: "10px",
    padding: "10px 15px",
    backgroundColor: "#f0f9ff",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#0369a1",
    flexWrap: "wrap"
  },
  kycHint: {
    fontSize: "13px",
    color: "#6b7280",
    marginBottom: "15px"
  },
  documentsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "15px"
  },
  documentItem: {
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "15px",
    backgroundColor: "#fafafa"
  },
  documentHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px"
  },
  documentIcon: {
    fontSize: "20px"
  },
  documentLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151"
  },
  requiredMark: {
    color: "#dc2626"
  },
  uploadArea: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "25px",
    border: "2px dashed #d1d5db",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
    backgroundColor: "white"
  },
  uploadContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    color: "#6b7280",
    fontSize: "13px"
  },
  uploadIcon: {
    fontSize: "24px"
  },
  previewContainer: {
    position: "relative",
    textAlign: "center"
  },
  imagePreview: {
    width: "100%",
    height: "120px",
    objectFit: "cover",
    borderRadius: "8px",
    border: "1px solid #e2e8f0"
  },
  pdfPreview: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    padding: "20px",
    backgroundColor: "#f3f4f6",
    borderRadius: "8px",
    fontSize: "12px",
    color: "#374151"
  },
  pdfIcon: {
    fontSize: "32px"
  },
  removeBtn: {
    marginTop: "8px",
    padding: "6px 12px",
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: "500"
  },
  submitSection: {
    display: "flex",
    justifyContent: "center",
    padding: "20px 0"
  },
  submitBtn: {
    padding: "16px 50px",
    backgroundColor: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s"
  },
  searchHint: {
    fontSize: "13px",
    color: "#6b7280",
    marginBottom: "15px"
  },
  searchBox: {
    display: "flex",
    gap: "10px"
  },
  searchInput: {
    flex: 1,
    padding: "14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "15px"
  },
  searchBtn: {
    padding: "14px 30px",
    backgroundColor: "#1e3a5f",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer"
  },
  resultsSection: {
    marginTop: "20px"
  },
  resultsTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "15px"
  },
  resultsList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  customerCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px",
    backgroundColor: "#f8fafc",
    borderRadius: "10px",
    border: "1px solid #e2e8f0"
  },
  customerInfo: {
    display: "flex",
    alignItems: "center",
    gap: "15px"
  },
  customerAvatar: {
    width: "45px",
    height: "45px",
    borderRadius: "50%",
    backgroundColor: "#1e3a5f",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    fontWeight: "600"
  },
  customerName: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#1f2937"
  },
  customerDetails: {
    fontSize: "12px",
    color: "#6b7280",
    marginTop: "3px"
  },
  customerAccount: {
    fontSize: "12px",
    color: "#1e3a5f",
    fontFamily: "monospace",
    marginTop: "3px"
  },
  customerActions: {
    display: "flex",
    gap: "8px"
  },
  selectBtn: {
    padding: "10px 20px",
    backgroundColor: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer"
  },
  noResults: {
    textAlign: "center",
    padding: "40px",
    color: "#6b7280"
  },
  noResultsIcon: {
    fontSize: "48px",
    display: "block",
    marginBottom: "10px"
  },
  createNewBtn: {
    marginTop: "15px",
    padding: "12px 25px",
    backgroundColor: "#1e3a5f",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  }
};
